import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BlogContentLanguage } from "../blogContent";
import { useUser } from "../contexts/UserContext";
import {
  AdminBlogPost,
  deleteAdminBlogPost,
  listAdminBlogPosts,
  setAdminBlogPostPublic,
} from "./firebaseFunctions";
import baseStyles from "./AdminKnowledgePage.module.css";
import styles from "./AdminBlogPage.module.css";

const previewLanguages: Array<{
  code: BlogContentLanguage;
  label: string;
}> = [
  {code: "ko", label: "한국어"},
  {code: "en", label: "English"},
  {code: "ja", label: "日本語"},
];

type VisibilityChange = {
  post: AdminBlogPost;
  public: boolean;
};

const sortPostsByNewestDate = (left: AdminBlogPost, right: AdminBlogPost) => {
  const dateDifference = right.date.localeCompare(left.date);
  if (dateDifference !== 0) {
    return dateDifference;
  }
  return right.updated.localeCompare(left.updated);
};

const AdminBlogPage: React.FC = () => {
  const userContext = useUser();
  const userDetails = userContext?.userDetails;
  const isAdmin =
    userDetails?.id !== "Guest" &&
    typeof userDetails?.userLevel === "number" &&
    userDetails.userLevel <= 1;
  const [posts, setPosts] = useState<AdminBlogPost[]>([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [previewLanguage, setPreviewLanguage] =
    useState<BlogContentLanguage>("ko");
  const [loading, setLoading] = useState(false);
  const [savingSlug, setSavingSlug] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [visibilityChange, setVisibilityChange] =
    useState<VisibilityChange | null>(null);
  const [visibilityError, setVisibilityError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminBlogPost | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deletingSlug, setDeletingSlug] = useState("");
  const visibilityDialogRef = useRef<HTMLDialogElement>(null);
  const deleteDialogRef = useRef<HTMLDialogElement>(null);
  const previewHeadingRef = useRef<HTMLHeadingElement>(null);
  const queueHeadingRef = useRef<HTMLHeadingElement>(null);
  const publishedHeadingRef = useRef<HTMLHeadingElement>(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const nextPosts = await listAdminBlogPosts();
      setPosts(nextPosts);
      setSelectedSlug((current) => {
        if (nextPosts.some((post) => post.slug === current)) {
          return current;
        }
        return "";
      });
    } catch (loadError: unknown) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "블로그 글을 불러오지 못했습니다."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "AUTOVID 블로그 글 관리";
    if (isAdmin) {
      void loadPosts();
    }
  }, [isAdmin, loadPosts]);

  useEffect(() => {
    const dialog = visibilityDialogRef.current;
    if (!dialog) {
      return;
    }
    if (visibilityChange && !dialog.open) {
      dialog.showModal();
    } else if (!visibilityChange && dialog.open) {
      dialog.close();
    }
  }, [visibilityChange]);

  useEffect(() => {
    const dialog = deleteDialogRef.current;
    if (!dialog) {
      return;
    }
    if (deleteTarget && !dialog.open) {
      dialog.showModal();
    } else if (!deleteTarget && dialog.open) {
      dialog.close();
    }
  }, [deleteTarget]);

  useEffect(() => {
    if (!selectedSlug) {
      return;
    }
    previewHeadingRef.current?.scrollIntoView({block: "start"});
    previewHeadingRef.current?.focus({preventScroll: true});
  }, [selectedSlug]);

  const showPreview = (post: AdminBlogPost) => {
    setSelectedSlug(post.slug);
    setPreviewLanguage("ko");
    requestAnimationFrame(() => {
      previewHeadingRef.current?.scrollIntoView({block: "start"});
      previewHeadingRef.current?.focus({preventScroll: true});
    });
  };

  const applyVisibilityChange = async () => {
    if (!visibilityChange || savingSlug) {
      return;
    }
    const {post, public: nextPublic} = visibilityChange;
    setSavingSlug(post.slug);
    setVisibilityError("");
    setError("");
    try {
      await setAdminBlogPostPublic(post.slug, nextPublic);
      setVisibilityChange(null);
      setStatus(
        nextPublic
          ? "글을 공개했습니다. sitemap.xml에는 즉시 반영되며, 정적 블로그는 최대 약 5분 안에 갱신됩니다."
          : "글을 비공개로 전환했습니다. sitemap.xml에서는 즉시 제외되며, 정적 블로그는 최대 약 5분 안에 갱신됩니다."
      );
      await loadPosts();
    } catch (saveError: unknown) {
      setVisibilityError(
        saveError instanceof Error
          ? saveError.message
          : "글 공개 상태를 변경하지 못했습니다."
      );
    } finally {
      setSavingSlug("");
    }
  };

  const selectedPost = posts.find((post) => post.slug === selectedSlug);
  const translation = selectedPost?.translations[previewLanguage];
  const queuedPosts = posts
    .filter((post) => !post.public)
    .sort(sortPostsByNewestDate);
  const publishedPosts = posts
    .filter((post) => post.public)
    .sort(sortPostsByNewestDate);

  const confirmDelete = async () => {
    if (!deleteTarget || deletingSlug) {
      return;
    }
    const slug = deleteTarget.slug;
    setDeletingSlug(slug);
    setDeleteError("");
    setError("");
    try {
      await deleteAdminBlogPost(slug);
      setDeleteTarget(null);
      setPosts((current) => current.filter((post) => post.slug !== slug));
      setSelectedSlug((current) => current === slug ? "" : current);
      setStatus("글을 삭제했습니다. 연결된 Cloudflare 이미지는 보존했습니다.");
      requestAnimationFrame(() => {
        if (deleteTarget.public) {
          publishedHeadingRef.current?.focus();
          return;
        }
        queueHeadingRef.current?.focus();
      });
    } catch (deleteRequestError: unknown) {
      setDeleteError(
        deleteRequestError instanceof Error
          ? deleteRequestError.message
          : "글을 삭제하지 못했습니다."
      );
    } finally {
      setDeletingSlug("");
    }
  };

  if (!isAdmin) {
    return (
      <div className={baseStyles.page}>
        <h1 className={baseStyles.title}>AUTOVID 블로그 글 관리</h1>
        <p className={baseStyles.error} role="alert">관리자 권한이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className={baseStyles.page}>
      <header className={baseStyles.header}>
        <p className={styles.eyebrow}>AUTOVID · EDITORIAL DESK</p>
        <h1 className={baseStyles.title}>블로그 글 관리</h1>
        <p className={baseStyles.lead}>
          자동 작성된 글은 비공개 상태로 저장됩니다. 검토 후 공개하면 사이트맵에는 즉시, 글별 정적 페이지에는 최대 약 5분 안에 반영됩니다.
        </p>
      </header>

      {status && <p className={baseStyles.status} role="status">{status}</p>}
      {error && <p className={baseStyles.error} role="alert">{error}</p>}

      <div className={styles.toolbar}>
        <span className={styles.repoChip}>
          공개 대기 {queuedPosts.length}건 · 공개 {publishedPosts.length}건
        </span>
        <button
          type="button"
          className={baseStyles.button}
          disabled={loading}
          onClick={() => void loadPosts()}
        >
          {loading ? "새로고침 중..." : "새로고침"}
        </button>
      </div>

      <section className={`${baseStyles.panel} ${styles.queuePanel}`} aria-labelledby="blog-queue-title">
        <div className={styles.sectionHeading}>
          <div>
            <p className={styles.sectionKicker}>PUBLICATION QUEUE</p>
            <h2
              className={baseStyles.panelTitle}
              id="blog-queue-title"
              ref={queueHeadingRef}
              tabIndex={-1}
            >
              공개 대기 글
            </h2>
          </div>
          <span className={styles.countBadge}>{queuedPosts.length}건</span>
        </div>
        {loading && posts.length === 0 ? (
          <p className={styles.empty} role="status">글을 불러오고 있습니다...</p>
        ) : queuedPosts.length === 0 ? (
          <p className={styles.empty}>공개 대기 중인 글이 없습니다.</p>
        ) : (
          <ul className={styles.reviewList}>
            {queuedPosts.map((post) => (
              <li className={styles.reviewItem} key={post.slug}>
                <div>
                  <span className={styles.privateBadge}>비공개</span>
                  <h3>{post.translations.ko.title}</h3>
                  <p>{post.date} · {post.slug}</p>
                </div>
                <div className={baseStyles.actions}>
                  <button
                    type="button"
                    className={baseStyles.button}
                    onClick={() => showPreview(post)}
                  >
                    미리보기
                  </button>
                  <button
                    type="button"
                    className={`${baseStyles.button} ${baseStyles.buttonPrimary}`}
                    disabled={savingSlug === post.slug}
                    onClick={() => {
                      setVisibilityError("");
                      setVisibilityChange({post, public: true});
                    }}
                  >
                    {savingSlug === post.slug ? "변경 중..." : "공개하기"}
                  </button>
                  <button
                    type="button"
                    className={`${baseStyles.button} ${styles.dangerButton}`}
                    disabled={Boolean(deletingSlug)}
                    onClick={() => {
                      setDeleteError("");
                      setDeleteTarget(post);
                    }}
                  >
                    삭제
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedPost && translation && (
        <section className={`${baseStyles.panel} ${styles.previewPanel}`} aria-labelledby="blog-preview-title">
          <div className={styles.previewHeader}>
            <div>
              <p className={styles.sectionKicker}>ARTICLE PREVIEW · {selectedPost.public ? "공개" : "비공개"}</p>
              <h2
                className={baseStyles.panelTitle}
                id="blog-preview-title"
                ref={previewHeadingRef}
                tabIndex={-1}
              >
                글 미리보기
              </h2>
            </div>
            <button type="button" className={baseStyles.button} onClick={() => setSelectedSlug("")}>
              미리보기 닫기
            </button>
          </div>

          <div className={styles.languageTabs} role="tablist" aria-label="미리보기 언어">
            {previewLanguages.map((language) => (
              <button
                type="button"
                role="tab"
                aria-selected={previewLanguage === language.code}
                className={previewLanguage === language.code ? styles.activeLanguageTab : styles.languageTab}
                key={language.code}
                onClick={() => setPreviewLanguage(language.code)}
              >
                {language.label}
              </button>
            ))}
          </div>

          <article className={styles.articlePreview} role="tabpanel">
            <header className={styles.articleHeader}>
              <p className={styles.articleMeta}>
                {selectedPost.date} · {selectedPost.author} · {selectedPost.tags.join(", ")}
              </p>
              <h2>{translation.title}</h2>
              <p className={styles.articleSummary}>{translation.summary}</p>
            </header>
            {(selectedPost.previewImageUrl || selectedPost.image?.path) && (
              <img
                src={selectedPost.previewImageUrl || selectedPost.image?.path}
                alt={selectedPost.image?.alt[previewLanguage] || ""}
                className={styles.previewImage}
              />
            )}
            <div className={styles.articleBody}>
              {translation.intro.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {translation.sections.map((section) => (
                <section key={section.heading}>
                  <h3>{section.heading}</h3>
                  {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </section>
              ))}
            </div>
          </article>
        </section>
      )}

      <section className={`${baseStyles.panel} ${styles.publishedPanel}`} aria-labelledby="published-posts-title">
        <div className={styles.sectionHeading}>
          <div>
            <p className={styles.sectionKicker}>PUBLISHED POSTS</p>
            <h2
              className={baseStyles.panelTitle}
              id="published-posts-title"
              ref={publishedHeadingRef}
              tabIndex={-1}
            >
              공개 글 목록
            </h2>
          </div>
          <span className={styles.countBadge}>{publishedPosts.length}건</span>
        </div>
        {loading && posts.length === 0 ? (
          <p className={styles.empty} role="status">글을 불러오고 있습니다...</p>
        ) : publishedPosts.length === 0 ? (
          <p className={styles.empty}>공개 중인 글이 없습니다.</p>
        ) : (
          <ul className={styles.reviewList}>
            {publishedPosts.map((post) => (
              <li className={styles.reviewItem} key={post.slug}>
                <div>
                  <span className={styles.publicBadge}>공개</span>
                  <h3>{post.translations.ko.title}</h3>
                  <p>{post.date} · {post.slug}</p>
                </div>
                <div className={baseStyles.actions}>
                  <button
                    type="button"
                    className={baseStyles.button}
                    onClick={() => showPreview(post)}
                  >
                    미리보기
                  </button>
                  <Link className={styles.linkButton} to={`/ko/blog/${post.slug}`}>
                    블로그에서 보기
                  </Link>
                  <button
                    type="button"
                    className={baseStyles.button}
                    disabled={savingSlug === post.slug}
                    onClick={() => {
                      setVisibilityError("");
                      setVisibilityChange({post, public: false});
                    }}
                  >
                    {savingSlug === post.slug ? "변경 중..." : "비공개"}
                  </button>
                  <button
                    type="button"
                    className={`${baseStyles.button} ${styles.dangerButton}`}
                    disabled={Boolean(deletingSlug)}
                    onClick={() => {
                      setDeleteError("");
                      setDeleteTarget(post);
                    }}
                  >
                    삭제
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <dialog
        ref={visibilityDialogRef}
        className={styles.dialog}
        aria-labelledby="visibility-dialog-title"
        aria-describedby="visibility-dialog-description"
        onCancel={(event) => {
          event.preventDefault();
          if (!savingSlug) {
            setVisibilityChange(null);
            setVisibilityError("");
          }
        }}
      >
        {visibilityChange && (
          <div>
            <h2 id="visibility-dialog-title">
              {visibilityChange.public ? "이 글을 공개할까요?" : "이 글을 비공개로 전환할까요?"}
            </h2>
            <p><strong>{visibilityChange.post.translations.ko.title}</strong></p>
            <p id="visibility-dialog-description">
              {visibilityChange.public
                ? "공개하면 다음 정적 배포에서 블로그 목록·상세 페이지와 sitemap.xml에 표시됩니다. 반영에는 최대 약 5분이 걸릴 수 있습니다."
                : "비공개로 전환하면 다음 정적 배포에서 블로그와 sitemap.xml에서 제외됩니다. 반영에는 최대 약 5분이 걸릴 수 있습니다."}
            </p>
            {visibilityError && <p className={styles.dialogError} role="alert">{visibilityError}</p>}
            <div className={baseStyles.actions}>
              <button
                type="button"
                className={baseStyles.button}
                disabled={Boolean(savingSlug)}
                onClick={() => {
                  setVisibilityChange(null);
                  setVisibilityError("");
                }}
              >
                취소
              </button>
              <button
                type="button"
                className={`${baseStyles.button} ${baseStyles.buttonPrimary}`}
                disabled={Boolean(savingSlug)}
                onClick={() => void applyVisibilityChange()}
              >
                {savingSlug
                  ? "변경 중..."
                  : visibilityChange.public
                    ? "공개하기"
                    : "비공개로 전환"}
              </button>
            </div>
          </div>
        )}
      </dialog>

      <dialog
        ref={deleteDialogRef}
        className={styles.dialog}
        aria-labelledby="delete-blog-post-title"
        aria-describedby="delete-blog-post-description"
        onCancel={(event) => {
          event.preventDefault();
          if (!deletingSlug) {
            setDeleteTarget(null);
            setDeleteError("");
          }
        }}
      >
        {deleteTarget && (
          <div>
            <h2 id="delete-blog-post-title">이 글을 삭제할까요?</h2>
            <p><strong>{deleteTarget.translations.ko.title}</strong></p>
            <p id="delete-blog-post-description">
              {deleteTarget.public
                ? "글은 목록과 공개 블로그, sitemap.xml에서 제거되며 복구할 수 없습니다."
                : "글은 공개 대기 목록에서 제거되며 복구할 수 없습니다."}
              연결된 Cloudflare 이미지는 삭제하지 않습니다.
            </p>
            {deleteError && <p className={styles.dialogError} role="alert">{deleteError}</p>}
            <div className={baseStyles.actions}>
              <button
                type="button"
                className={baseStyles.button}
                disabled={Boolean(deletingSlug)}
                onClick={() => {
                  setDeleteTarget(null);
                  setDeleteError("");
                }}
              >
                취소
              </button>
              <button
                type="button"
                className={`${baseStyles.button} ${styles.dangerButton}`}
                disabled={Boolean(deletingSlug)}
                onClick={() => void confirmDelete()}
              >
                {deletingSlug ? "삭제 중..." : "글 삭제"}
              </button>
            </div>
          </div>
        )}
      </dialog>
    </div>
  );
};

export default AdminBlogPage;
